import { useState, useEffect, useCallback } from "react";
import { useDisclosure } from "@mantine/hooks";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";

export type CollaborationPaymentSuccessData = {
  transactionId: string;
  creditsCharged: number;
  date: string;
  paymentMethod: string;
  newStatus: string;
  /** User checked the NDA box on the payment modal for this charge. */
  acceptedNdaWithPayment?: boolean;
  /** Server created/signed NDA so both parties are fully signed (only when BOTH_PAID). */
  bothNdaBecameSigned?: boolean;
};

export function useCollaborationEngagementPayment(onFlowComplete?: () => void) {
  const [matchId, setMatchId] = useState<string | null>(null);
  const [paymentModalOpened, { open: openPaymentModal, close: closePaymentModal }] = useDisclosure(false);
  const [successData, setSuccessData] = useState<CollaborationPaymentSuccessData | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [engagementFeeCredits, setEngagementFeeCredits] = useState(14);

  useEffect(() => {
    trpc.paymentConfig.get.query().then((config) => {
      setEngagementFeeCredits(config.engagementFeeCredits);
    });
  }, []);

  const beginEngagementPayment = useCallback(
    (id: string) => {
      setMatchId(id);
      openPaymentModal();
    },
    [openPaymentModal],
  );

  const handlePaymentModalClose = useCallback(() => {
    closePaymentModal();
    setMatchId(null);
  }, [closePaymentModal]);

  const handlePaymentConfirm = useCallback(async (payload?: { method?: "credits"; acceptNda?: boolean }) => {
    if (!matchId) return;
    const idForPayment = matchId;
    const acceptNda = !!payload?.acceptNda;
    setPaymentLoading(true);
    try {
      const result = await trpc.collaboration.payEngagementFee.mutate({
        matchId: idForPayment,
        acceptNda,
      });
      if (result.success) {
        closePaymentModal();
        setMatchId(null);

        const paymentPayload: CollaborationPaymentSuccessData = {
          transactionId: `TXN-${new Date().getFullYear()}-${Date.now().toString().slice(-8)}`,
          creditsCharged: result.creditsCharged,
          date: new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }),
          paymentMethod: "Collaboration Credits",
          newStatus: result.newStatus,
          acceptedNdaWithPayment: acceptNda,
          bothNdaBecameSigned: Boolean((result as { bothNdaBecameSigned?: boolean }).bothNdaBecameSigned),
        };
        setSuccessData(paymentPayload);

        const paymentMessage =
          result.newStatus === "BOTH_PAID"
            ? `${result.creditsCharged} credits charged from your wallet. Collaboration is active.`
            : result.creditsCharged > 0
              ? `${result.creditsCharged} credits charged. Waiting for the other party to pay.`
              : "No credits charged yet — both wallets are debited only after the other company pays too. Waiting for the other party.";

        successNotification({
          title: result.newStatus === "BOTH_PAID" ? "Payment Successful" : "Payment step recorded",
          message: paymentMessage,
        });

        onFlowComplete?.();
      }
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setPaymentLoading(false);
    }
  }, [matchId, closePaymentModal, onFlowComplete]);

  const handleSuccessModalClose = useCallback(() => {
    setSuccessData(null);
  }, []);

  return {
    engagementFeeCredits,
    paymentModalOpened,
    paymentLoading,
    beginEngagementPayment,
    handlePaymentModalClose,
    handlePaymentConfirm,
    successData,
    handleSuccessModalClose,
  };
}
